import React, { useState } from "react";
import moment from "moment";
import { enqueueSnackbar } from "notistack";
import CommonFreeTextInput from "../common/inputs/CommonFreeTextInput";
import CommonDropdownInput from "../common/inputs/CommonDropdownInput";
import CommonDatePicker from "../common/inputs/CommonDatePicker";
import CommonTextAreaInput from "../common/inputs/CommonTextAreaInput";
import CommonCountryInput from "../common/inputs/CommonCountryInput";
import CommonPhoneNumberInput from "../common/CommonPhoneNumberInput";
import CommonInputContainer from "../common/inputs/CommonInputContainer";

const TITLE_OPTIONS = ["Mr", "Mrs", "Ms", "Miss", "Dr"];
const GENDER_OPTIONS = ["Male", "Female"];
const RELATIONSHIP_OPTIONS = [
  "Wife",
  "Husband",
  "Father",
  "Mother",
  "Brother",
  "Sister",
  "Son",
  "Daughter",
  "Friend",
  "Other",
];

const REQUIRED_PERSON_FIELDS = [
  "title",
  "gender",
  "firstName",
  "lastName",
  "birthDate",
  "email",
  "phone",
  "passportNumber",
  "nextOfKinName",
  "nextOfKinRelationship",
  "nextOfKinContact",
  "nextOfKinAddress",
];

function cloneBody(body) {
  return JSON.parse(JSON.stringify(body || {}));
}

function parsePhone(phone = "") {
  const parts = String(phone).split("-");
  if (parts.length > 1) {
    return {
      countryCode: parts[0] || "234",
      phoneNumber: parts.slice(1).join("") || "",
    };
  }
  return { countryCode: "234", phoneNumber: String(phone).replace(/\D/g, "") };
}

function joinPhone({ countryCode, phoneNumber }) {
  if (!phoneNumber) return "";
  return `${countryCode || "234"}-${phoneNumber}`;
}

function toDate(value) {
  if (!value) return null;
  const date = moment(value);
  return date.isValid() ? date.toDate() : null;
}

function fromDate(date) {
  return date ? moment(date).format("YYYY-MM-DD") : "";
}

function PhoneField({ label, value, onChange, error }) {
  const parsed = parsePhone(value);
  return (
    <CommonInputContainer label={label} error={error} className="flex-1">
      <CommonPhoneNumberInput
        countryCode={parsed.countryCode}
        phoneNumber={parsed.phoneNumber}
        onChange={(next) => onChange(joinPhone(next))}
        error={error}
      />
    </CommonInputContainer>
  );
}

export default function RebookInsuranceDetailsForm({
  requestBody,
  onCancel,
  onContinue,
}) {
  const [draft, setDraft] = useState(() => cloneBody(requestBody));
  const [openTravellers, setOpenTravellers] = useState({ 0: true });
  const [errors, setErrors] = useState({});

  const persons = Array.isArray(draft?.personsInfo) ? draft.personsInfo : [];

  const updatePerson = (index, field, value) => {
    setDraft((prev) => ({
      ...prev,
      personsInfo: (prev.personsInfo || []).map((person, i) =>
        i === index ? { ...person, [field]: value } : person,
      ),
    }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[`${index}.${field}`];
      return next;
    });
  };

  const updateMedical = (personIndex, medicalIndex, answer) => {
    setDraft((prev) => ({
      ...prev,
      personsInfo: (prev.personsInfo || []).map((person, i) => {
        if (i !== personIndex) return person;
        return {
          ...person,
          medical: (person.medical || []).map((item, mi) =>
            mi === medicalIndex ? { ...item, answer } : item,
          ),
        };
      }),
    }));
  };

  const validate = () => {
    const nextErrors = {};

    persons.forEach((person, index) => {
      REQUIRED_PERSON_FIELDS.forEach((field) => {
        if (!String(person?.[field] || "").trim()) {
          nextErrors[`${index}.${field}`] = true;
        }
      });
      (person?.medical || []).forEach((item, medicalIndex) => {
        if (item?.answer == null || String(item.answer).trim() === "") {
          nextErrors[`${index}.medical.${medicalIndex}`] = true;
        }
      });
    });

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleContinue = () => {
    if (!validate()) {
      enqueueSnackbar("Fill in the required traveller details to continue.", {
        variant: "warning",
      });
      return;
    }
    onContinue(draft);
  };

  return (
    <div className="w-full flex flex-col gap-6">
      <div className="flex flex-col gap-3">
        {persons.map((person, index) => {
          const open = !!openTravellers[index];
          return (
            <div
              key={index}
              className="rounded-lg border border-gray-300 overflow-hidden"
            >
              <button
                type="button"
                className="w-full px-4 py-3 flex items-center gap-3 bg-[#F0F6FF]"
                onClick={() =>
                  setOpenTravellers((prev) => ({
                    ...prev,
                    [index]: !prev[index],
                  }))
                }
              >
                <div className="w-6 h-6 text-white bg-primary1 font-bold text-sm rounded-md flex items-center justify-center">
                  {index + 1}
                </div>
                <span className="font-semibold text-sm">
                  {person?.title} {person?.lastName}/{person?.firstName}{" "}
                  {person?.middleName}
                </span>
                <span className="ml-auto text-primary1 text-sm">
                  {open ? "Hide" : "Edit"}
                </span>
              </button>
              {open ? (
                <div className="p-4 flex flex-col gap-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <CommonDropdownInput
                      label="Title"
                      options={TITLE_OPTIONS}
                      value={person?.title || ""}
                      onChange={(value) => updatePerson(index, "title", value)}
                      error={errors[`${index}.title`]}
                    />
                    <CommonDropdownInput
                      label="Gender"
                      options={GENDER_OPTIONS}
                      value={person?.gender || ""}
                      onChange={(value) => updatePerson(index, "gender", value)}
                      error={errors[`${index}.gender`]}
                    />
                    <CommonFreeTextInput
                      label="First name"
                      value={person?.firstName || ""}
                      onChange={(value) =>
                        updatePerson(index, "firstName", value)
                      }
                      error={errors[`${index}.firstName`]}
                    />
                    <CommonFreeTextInput
                      label="Middle name"
                      value={person?.middleName || ""}
                      onChange={(value) =>
                        updatePerson(index, "middleName", value)
                      }
                    />
                    <CommonFreeTextInput
                      label="Last name"
                      value={person?.lastName || ""}
                      onChange={(value) =>
                        updatePerson(index, "lastName", value)
                      }
                      error={errors[`${index}.lastName`]}
                    />
                    <CommonDatePicker
                      label="Date of birth"
                      value={toDate(person?.birthDate)}
                      onChange={(date) =>
                        updatePerson(index, "birthDate", fromDate(date))
                      }
                      error={errors[`${index}.birthDate`]}
                      maxDate={new Date()}
                    />
                    <CommonFreeTextInput
                      label="Email"
                      value={person?.email || ""}
                      onChange={(value) => updatePerson(index, "email", value)}
                      error={errors[`${index}.email`]}
                    />
                    <PhoneField
                      label="Phone"
                      value={person?.phone || ""}
                      onChange={(value) => updatePerson(index, "phone", value)}
                      error={errors[`${index}.phone`]}
                    />
                    <CommonFreeTextInput
                      label="City"
                      value={person?.city || ""}
                      onChange={(value) => updatePerson(index, "city", value)}
                    />
                    <CommonFreeTextInput
                      label="State"
                      value={person?.state || ""}
                      onChange={(value) => updatePerson(index, "state", value)}
                    />
                    <CommonCountryInput
                      label="Nationality"
                      value={person?.countryCode || ""}
                      onChange={(value) =>
                        updatePerson(index, "countryCode", value)
                      }
                    />
                    <CommonFreeTextInput
                      label="Passport number"
                      value={person?.passportNumber || ""}
                      onChange={(value) =>
                        updatePerson(index, "passportNumber", value)
                      }
                      error={errors[`${index}.passportNumber`]}
                    />
                    <CommonFreeTextInput
                      label="NIN"
                      value={person?.nin || ""}
                      onChange={(value) => updatePerson(index, "nin", value)}
                    />
                    <CommonFreeTextInput
                      label="Occupation"
                      value={person?.occupation || ""}
                      onChange={(value) =>
                        updatePerson(index, "occupation", value)
                      }
                    />
                  </div>

                  <span className="font-medium text-sm mt-2">Next of kin</span>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <CommonFreeTextInput
                      label="Name"
                      value={person?.nextOfKinName || ""}
                      onChange={(value) =>
                        updatePerson(index, "nextOfKinName", value)
                      }
                      error={errors[`${index}.nextOfKinName`]}
                    />
                    <CommonDropdownInput
                      label="Relationship"
                      options={RELATIONSHIP_OPTIONS}
                      value={person?.nextOfKinRelationship || ""}
                      onChange={(value) =>
                        updatePerson(index, "nextOfKinRelationship", value)
                      }
                      error={errors[`${index}.nextOfKinRelationship`]}
                    />
                    <PhoneField
                      label="Phone"
                      value={person?.nextOfKinContact || ""}
                      onChange={(value) =>
                        updatePerson(index, "nextOfKinContact", value)
                      }
                      error={errors[`${index}.nextOfKinContact`]}
                    />
                    <CommonTextAreaInput
                      label="Address"
                      value={person?.nextOfKinAddress || ""}
                      onChange={(value) =>
                        updatePerson(index, "nextOfKinAddress", value)
                      }
                      error={errors[`${index}.nextOfKinAddress`]}
                    />
                  </div>

                  {(person?.medical || []).length ? (
                    <div className="flex flex-col gap-3 mt-2">
                      <span className="font-medium text-sm">Medical</span>
                      {person.medical.map((item, medicalIndex) => (
                        <CommonTextAreaInput
                          key={medicalIndex}
                          label={
                            item?.question || `Question ${medicalIndex + 1}`
                          }
                          value={
                            item?.answer == null ? "" : String(item.answer)
                          }
                          onChange={(value) =>
                            updateMedical(index, medicalIndex, value)
                          }
                          error={errors[`${index}.medical.${medicalIndex}`]}
                        />
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="flex gap-4 items-center">
        <button
          type="button"
          onClick={onCancel}
          className="py-4 px-6 text-xs md:text-lg font-bold w-full max-w-[174px]"
        >
          Back
        </button>
        <button
          type="button"
          className="bg-primary1 flex items-center justify-center text-white w-full py-4 rounded-lg text-xs sm:text-lg"
          onClick={handleContinue}
        >
          Continue to rebook
        </button>
      </div>
    </div>
  );
}
